// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type SeverityLevel = {
  key: string;
  label: string;
  color: string;
};
export const SeverityLevelSchema = z.object({
  key: z.string().min(1, "Severity key is required").max(40).regex(new RegExp("^[a-z0-9][a-z0-9_-]*$"), "Severity key must be lowercase letters, numbers, - or _"),
  label: z.string().min(1, "Severity label is required").max(80),
  color: z.string().regex(new RegExp("^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$"), "Color must be a hex value like #b91c1c"),
}).strict();

export type InspectionPipeline = {
  key: string;
  name: string;
  stages: Array<string>;
};
export const InspectionPipelineSchema = z.object({
  key: z.string().min(1).max(60).default("default"),
  name: z.string().min(1).max(120).default("Default"),
  stages: z.array(z.string().min(1).max(60)).default([]),
}).strict();

export type TypeEligibility = {
  canPerform: boolean;
  canApprove: boolean;
  reasons: Array<"permission_missing" | "role_not_permitted" | "type_inactive" | "no_selectable_template">;
};
export const TypeEligibilitySchema = z.object({
  canPerform: z.boolean(),
  canApprove: z.boolean(),
  reasons: z.array(z.enum(["permission_missing", "role_not_permitted", "type_inactive", "no_selectable_template"] as const)),
}).strict();

export type TemplateCheck = {
  templateId: string;
  templateName: string;
  version: number | null;
  selectable: boolean;
  reason: string | null;
  missingFields: Array<string>;
};
export const TemplateCheckSchema = z.object({
  templateId: z.string(),
  templateName: z.string(),
  version: z.number().nullable(),
  selectable: z.boolean(),
  reason: z.string().nullable(),
  missingFields: z.array(z.string()),
}).strict();

export type TemplateFixRow = {
  templateId: string;
  templateName: string;
  version: number | null;
  field: string;
  message: string;
};
export const TemplateFixRowSchema = z.object({
  templateId: z.string(),
  templateName: z.string(),
  version: z.number().nullable(),
  field: z.string(),
  message: z.string(),
}).strict();

export type TypeValidation = {
  ok: boolean;
  templates: Array<TemplateCheck>;
  missing: Array<TemplateFixRow>;
  activationBlockers: Array<string>;
};
export const TypeValidationSchema = z.object({
  ok: z.boolean(),
  templates: z.array(TemplateCheckSchema),
  missing: z.array(TemplateFixRowSchema),
  activationBlockers: z.array(z.string()),
}).strict();

export type InspectionTypeSnapshot = {
  id: string;
  version: number;
  name: string;
  domainKey: string;
  slaHours: number | null;
  severityScale: Array<SeverityLevel>;
  capturedAt: string;
};
export const InspectionTypeSnapshotSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  name: z.string(),
  domainKey: z.string(),
  slaHours: z.number().int().nullable(),
  severityScale: z.array(SeverityLevelSchema),
  capturedAt: z.string(),
}).strict();

export type InspectionType = {
  _id: string;
  organizationId: string;
  domainKey: string;
  name: string;
  description?: string | null | undefined;
  templateIds: Array<string>;
  requiredFields: Array<string>;
  severityScale: Array<SeverityLevel>;
  slaHours: number | null;
  allowedRoleIds: {
  perform: Array<string>;
  approve: Array<string>;
};
  active: boolean;
  version: number;
  pipeline: InspectionPipeline;
  standards: Array<string>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
  eligibility: TypeEligibility;
};
export const InspectionTypeSchema = z.object({
  _id: z.string(),
  organizationId: z.string(),
  domainKey: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  templateIds: z.array(z.string()),
  requiredFields: z.array(z.string()),
  severityScale: z.array(SeverityLevelSchema),
  slaHours: z.number().int().nullable(),
  allowedRoleIds: z.object({
    perform: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).default([]),
    approve: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).default([]),
  }).strict(),
  active: z.boolean(),
  version: z.number().int(),
  pipeline: InspectionPipelineSchema,
  standards: z.array(z.string()),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  usageCount: z.number().int(),
  eligibility: TypeEligibilitySchema,
}).strict();

export type CreateInspectionTypeBody = {
  domainKey: string;
  name: string;
  description?: string | null | undefined;
  templateIds?: Array<string> | undefined;
  requiredFields?: Array<string> | undefined;
  severityScale?: Array<SeverityLevel> | undefined;
  slaHours?: number | null | undefined;
  allowedRoleIds?: {
  perform?: Array<string> | undefined;
  approve?: Array<string> | undefined;
} | undefined;
  active?: boolean | undefined;
  standards?: Array<string> | undefined;
};
export const CreateInspectionTypeBodySchema = z.object({
  domainKey: z.string().min(1, "Domain is required"),
  name: z.string().min(1, "Name is required").max(200),
  description: z.string().max(2000).nullable().optional(),
  templateIds: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).default([]),
  requiredFields: z.array(z.string().min(1).max(120)).default([]),
  severityScale: z.array(SeverityLevelSchema).default([]),
  slaHours: z.number().int().min(0, "SLA hours cannot be negative").max(87600).nullable().optional(),
  allowedRoleIds: z.object({
    perform: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).default([]),
    approve: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).default([]),
  }).strict().default({"perform":[],"approve":[]}),
  active: z.boolean().default(false),
  standards: z.array(z.string().min(1).max(120)).default([]),
}).strict();

export type UpdateInspectionTypeBody = {
  name?: string | undefined;
  description?: string | null | undefined;
  templateIds?: Array<string> | undefined;
  requiredFields?: Array<string> | undefined;
  severityScale?: Array<SeverityLevel> | undefined;
  slaHours?: number | null | undefined;
  allowedRoleIds?: {
  perform?: Array<string> | undefined;
  approve?: Array<string> | undefined;
} | undefined;
  active?: boolean | undefined;
  standards?: Array<string> | undefined;
};
export const UpdateInspectionTypeBodySchema = z.object({
  name: z.string().min(1, "Name is required").max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  templateIds: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).optional(),
  requiredFields: z.array(z.string().min(1).max(120)).optional(),
  severityScale: z.array(SeverityLevelSchema).optional(),
  slaHours: z.number().int().min(0, "SLA hours cannot be negative").max(87600).nullable().optional(),
  allowedRoleIds: z.object({
    perform: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).default([]),
    approve: z.array(z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId")).default([]),
  }).strict().optional(),
  active: z.boolean().optional(),
  standards: z.array(z.string().min(1).max(120)).optional(),
}).strict().refine((body) => Object.keys(body).length > 0);

export type ListInspectionTypesResponse = {
  items: Array<InspectionType>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListInspectionTypesQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  q: z.string().min(1).optional(),
  domain: z.string().min(1).optional(),
  status: z.enum(["active", "inactive", "all"] as const).default("all"),
  performable: z.enum(["true", "false"] as const).optional(),
});

export async function listInspectionTypes(query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  q?: string | undefined;
  domain?: string | undefined;
  status?: "active" | "inactive" | "all" | undefined;
  performable?: "true" | "false" | undefined;
}): Promise<ListInspectionTypesResponse> {
  const response = await api.get<ListInspectionTypesResponse>(`/inspection-types`, { params: query });
  return response.data;
}

export type CreateInspectionTypeResponse = {
  data: InspectionType;
};

export async function createInspectionType(body: CreateInspectionTypeBody): Promise<CreateInspectionTypeResponse> {
  const response = await api.post<CreateInspectionTypeResponse>(`/inspection-types`, body);
  return response.data;
}

export type GetInspectionTypeResponse = {
  data: InspectionType;
};

export const GetInspectionTypeParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getInspectionType(id: string): Promise<GetInspectionTypeResponse> {
  const response = await api.get<GetInspectionTypeResponse>(`/inspection-types/${encodeURIComponent(id)}`);
  return response.data;
}

export type UpdateInspectionTypeResponse = {
  data: InspectionType;
};

export const UpdateInspectionTypeParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function updateInspectionType(id: string, body: UpdateInspectionTypeBody): Promise<UpdateInspectionTypeResponse> {
  const response = await api.patch<UpdateInspectionTypeResponse>(`/inspection-types/${encodeURIComponent(id)}`, body);
  return response.data;
}

export type GetInspectionTypeValidationResponse = {
  data: TypeValidation;
};

export const GetInspectionTypeValidationParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getInspectionTypeValidation(id: string): Promise<GetInspectionTypeValidationResponse> {
  const response = await api.get<GetInspectionTypeValidationResponse>(`/inspection-types/${encodeURIComponent(id)}/validation`);
  return response.data;
}

export const inspectionTypesKeys = {
  all: ["inspectionTypes"] as const,
  lists: () => [...inspectionTypesKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...inspectionTypesKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...inspectionTypesKeys.all, 'detail'] as const,
  detail: (id: string) => [...inspectionTypesKeys.details(), id] as const,
};
